const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

function throttle(fn, wait) {
  let time = Date.now();
  return function() {
    if ((time + wait - Date.now()) < 0) {
      fn();
      time = Date.now();
    }
  }
}

function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    return 100;
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = (distance / (viewportHeight + elementHeight)) * 100;
  return Math.min(100, Math.max(0, percentage));
}

function initializeHeroCardsInteraction() {
  const cards = document.querySelectorAll('.farmacia-hero-card');
  const radicalTexts = document.querySelectorAll('.radical-display-text');
  
  if (cards.length) {
    cards.forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const moveX = (x / rect.width) * 100;
        const moveY = (y / rect.height) * 100;
        card.style.setProperty('--mouse-x', `${moveX}%`);
        card.style.setProperty('--mouse-y', `${moveY}%`);
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const rotateX = (y - centerY) / 100;
        const rotateY = (centerX - x) / 100;
        
        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.005, 1.005, 1.005)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
        card.style.setProperty('--mouse-x', `50%`);
        card.style.setProperty('--mouse-y', `50%`);
      });
    });
  }

  if (radicalTexts.length) {
    window.addEventListener('scroll', throttle(() => {
      const scrolled = window.scrollY;
      radicalTexts.forEach((text, index) => {
        const speed = (index + 1) * 0.2;
        const direction = index === 0 ? -1 : 1;
        text.style.transform = `translate3d(${scrolled * speed * direction}px, 0, 0)`;
      });
    }, 10), { passive: true });
  }
}

function initializeHorizontalScroll() {
  const wrapper = document.querySelector('.horizontal-scroll-wrapper');
  const content = document.querySelector('.horizontal-scroll-content');
  if (!wrapper || !content) return;
  const DESKTOP_BREAKPOINT = 750;

  const resetCompactLayout = () => {
    content.style.transform = '';
  };

  const isDesktopViewport = () => window.innerWidth >= DESKTOP_BREAKPOINT;

  const handleScroll = () => {
    if (!isDesktopViewport()) {
      resetCompactLayout();
      return;
    }

    const rect = wrapper.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    
    const totalScrollableHeight = rect.height - windowHeight;
    if (totalScrollableHeight <= 0) return;

    let progress = -rect.top / totalScrollableHeight;
    progress = Math.max(0, Math.min(1, progress));

    const deadzone = 0;
    let adjustedProgress = 0;
    if (progress > deadzone) {
      adjustedProgress = (progress - deadzone) / (1 - deadzone);
    }
    
    const contentWidth = content.scrollWidth;
    const viewportWidth = window.innerWidth;
    const maxMove = Math.max(0, contentWidth - viewportWidth + (viewportWidth * 0.1));
    
    const moveX = adjustedProgress * maxMove;
    content.style.transform = `translate3d(${-moveX}px, 0, 0)`;
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleScroll);
  
  const resizeObserver = new ResizeObserver(() => handleScroll());
  resizeObserver.observe(content);
  resizeObserver.observe(wrapper);
  
  handleScroll();
}

function initializeAddToCartButtons() {
  if (typeof gsap === 'undefined') return;

  document.querySelectorAll('.add-to-cart').forEach(button => {
    if (button.dataset.initialized) return;
    button.dataset.initialized = "true";

    button.addEventListener('pointerdown', e => {
      if(button.classList.contains('active')) return;
      gsap.to(button, { '--background-scale': .97, duration: .15 });
    });

    button.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      
      if(button.classList.contains('active')) return;
      button.classList.add('active');
      
      const productId = button.getAttribute('data-product-id');
      
      gsap.to(button, {
        keyframes: [
          { '--background-scale': .97, duration: .15 },
          { '--background-scale': 1, delay: .125, duration: 1.2, ease: 'elastic.out(1, .6)' }
        ]
      });

      gsap.to(button, {
        keyframes: [
          { '--shirt-scale': 1, '--shirt-y': '-120px', '--cart-x': '0px', '--cart-scale': 1, duration: .4, ease: 'power1.in' },
          { '--shirt-y': '-115px', duration: .3 },
          { '--shirt-y': '12px', '--shirt-scale': .9, duration: .25, ease: 'none' },
          { '--shirt-scale': 0, duration: .3, ease: 'none' }
        ]
      });

      gsap.to(button, {
        keyframes: [
          { '--text-x': '0px', '--text-o': 0, duration: .3, ease: 'power2.in' },
          { '--cart-rotate': '-12deg', '--cart-clip': '12px', '--cart-clip-x': '2px', duration: .3, delay: .3 },
          { '--cart-y': '-1px', '--cart-rotate': '0deg', '--cart-clip': '0px', '--cart-clip-x': '0px', duration: .2, ease: 'power2.out' },
          { '--cart-x': '150px', '--cart-scale': 0, duration: .4, delay: .3, ease: 'power2.in' },
          { '--text-x': '0px', '--text-o': 1, duration: .3, delay: .1, onStart: () => {
              const span = button.querySelector('span');
              if (!button.dataset.originalText) {
                button.dataset.originalText = span.innerText;
              }
              span.innerText = '¡Agregado!';
              setTimeout(() => {
                const resetTl = gsap.timeline({
                  onComplete: () => {
                    button.classList.remove('active');
                  }
                });

                resetTl.to(button, {
                  '--cart-x': '0px',
                  '--cart-scale': 0.85,
                  '--text-o': 0,
                  duration: 0.3,
                  ease: 'power2.out'
                })
                .add(() => {
                  span.innerText = button.dataset.originalText;
                })
                .to(button, {
                  '--cart-x': '-30px',
                  '--text-x': '12px',
                  '--text-o': 1,
                  '--background-scale': 1,
                  '--shirt-y': '0px',
                  '--shirt-scale': 0,
                  '--cart-y': '0px',
                  '--cart-rotate': '0deg',
                  '--cart-clip': '0px',
                  '--cart-clip-x': '0px',
                  '--cart-tick-offset': '10px',
                  duration: 0.4,
                  ease: 'power2.inOut'
                });
              }, 1000);
            }
          }
        ]
      });

      gsap.to(button, { '--cart-tick-offset': 0, duration: .25, delay: 1.1 });

      if (productId) {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartSurface = cartDrawer || document.querySelector('cart-notification');
        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
        };

        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const requestBody = new FormData();
        requestBody.append('id', productId);
        requestBody.append('quantity', '1');

        if (cartSurface && typeof cartSurface.getSectionsToRender === 'function') {
          requestBody.append(
            'sections',
            cartSurface.getSectionsToRender().map((section) => section.id)
          );
          requestBody.append('sections_url', window.location.pathname);

          if (typeof cartSurface.setActiveElement === 'function') {
            cartSurface.setActiveElement(button);
          }
        }

        config.body = requestBody;

        fetch(window.routes.cart_add_url, config)
        .then(async (response) => {
          if (isLocalPreviewCartChallenge(response.status)) {
            submitCartAddFallback(productId, 1);
            return { previewFallback: true };
          }

          let data;

          try {
            data = await response.json();
          } catch (error) {
            throw new Error('Shopify devolvio una respuesta invalida al intentar agregar el producto al carrito.');
          }

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'No se pudo agregar el producto al carrito.');
          }

          return data;
        })
        .then(data => {
          if (data?.previewFallback) return;

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: productId, cartData: data });
          } else if (window.pubsub) {
            window.pubsub.publish(window.pubsub.events.cartUpdate, { source: 'product-form', productVariantId: productId, cartData: data });
          }

          if (cartDrawer && data.sections && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
          } else if (cartDrawer) {
            const cartUrl = window.routes ? window.routes.cart_url : '/cart';
            fetch(`${cartUrl}?section_id=cart-drawer`)
              .then((response) => response.text())
              .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');
                const sourceCartDrawer = html.querySelector('cart-drawer');
                const sourceDrawerItems = html.querySelector('cart-drawer-items');

                const selectors = [
                  '.drawer__header',
                  'cart-drawer-items',
                  '.drawer__footer',
                  '.cart-drawer__footer',
                  '#CartDrawer-CartItems',
                  '#cart-icon-bubble',
                ];
                selectors.forEach(selector => {
                  const targetElement = document.querySelector(selector);
                  const sourceElement = html.querySelector(selector);
                  if (targetElement && sourceElement) {
                    targetElement.innerHTML = sourceElement.innerHTML;
                  }
                });

                if (sourceCartDrawer) {
                  cartDrawer.className = sourceCartDrawer.className;
                } else {
                  cartDrawer.classList.remove('is-empty');
                }
                const drawerInner = cartDrawer.querySelector('.drawer__inner');
                if (drawerInner) drawerInner.classList.remove('is-empty');
                const drawerItems = cartDrawer.querySelector('cart-drawer-items');
                if (drawerItems) {
                  if (sourceDrawerItems) {
                    drawerItems.className = sourceDrawerItems.className;
                  } else {
                    drawerItems.classList.remove('is-empty');
                  }
                }

                if (typeof cartDrawer.open === 'function') {
                  cartDrawer.open();
                } else {
                  cartDrawer.classList.add('animate', 'active');
                }
              })
              .catch(() => {
                if (typeof cartDrawer.open === 'function') cartDrawer.open();
              });
          } else {
            window.location = window.routes.cart_url;
          }
        })
        .catch((error) => {
          console.error('Error al agregar al carrito desde la card de producto:', error);
        });
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  initializeHeroCardsInteraction();
  initializeHorizontalScroll();
  initializeAddToCartButtons();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    initializeHeroCardsInteraction();
    initializeHorizontalScroll();
    initializeAddToCartButtons();
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    initializeHorizontalScroll();
    initializeAddToCartButtons();
  });
}
